import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Plug, Plus, Search, Globe2, Activity, Inbox, ShieldCheck,
  CheckCircle2, AlertTriangle, Clock, MoreHorizontal, ArrowRight, X,
  Building2, Mail, User, Hash, MapPin as MapPinIcon, Sparkles,
} from "lucide-react";
import { Topbar } from "@/components/dashboard/Topbar";
import { GlassPanel } from "@/components/dashboard/GlassPanel";
import { WorldMap, REGION_COORDS, type MapPin } from "@/components/dashboard/WorldMap";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { APIError, apiRequest } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { daemonAccountDisplayName, fetchDaemonAccount } from "@/lib/daemon-account";

export const Route = createFileRoute("/connect")({
  head: () => ({ meta: [{ title: "Connect · GENRL" }] }),
  component: ConnectPage,
});

type Connection = {
  id: string;
  spokeID?: string;
  peerID?: string;
  partner: string;
  company: string;
  region: string;
  available: boolean;
  state: "active" | "pending" | "disconnected";
  revoked: boolean;
  lastChecked: string;
};

type Invite = { id: string; from: string; company: string; region: string; received: string };

type PeerCard = {
  id: string;
  name: string;
  company: string;
  region: string;
  gpus?: number;
  latencyMs?: number;
  online: boolean;
};

type BackendPeer = {
  peer_id: string;
  node_id?: string;
  node_type?: string;
  api_base_url?: string;
  connected?: boolean;
  last_seen?: string;
};

type IncomingRequestRow = {
  request_id: string;
  from_hub_id?: string;
  from_peer_id?: string;
  state?: string;
  received_at?: string;
};

type ConnectionRequestRow = {
  request_id: string;
  ack_id?: string;
  direction?: string;
  partner_peer_id?: string;
  source_peer_id?: string;
  source_hub_id?: string;
  target_peer_id?: string;
  state?: string;
  invite_expires?: string;
  created_at?: string;
  updated_at?: string;
};

type CapacitySignal = {
  spoke_id?: string;
  avail_count?: number;
};

type CapacityEntry = {
  signal?: CapacitySignal;
};

function fmtWhen(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function humanSince(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function regionFromPeer(peer: BackendPeer): string {
  const base = String(peer.api_base_url || "").toLowerCase();
  if (base.includes("eu") || base.includes("london") || base.includes("frankfurt")) return "EU";
  if (base.includes("us")) return "US";
  if (base.includes("ap") || base.includes("asia")) return "APAC";
  return "Global";
}

function isHubLikePeer(nodeType?: string): boolean {
  const normalized = String(nodeType || "").trim().toLowerCase();
  return [
    "hub",
    "hub_only",
    "hub-only",
    "hybrid",
    "hub_and_spoke",
    "hub-and-spoke",
  ].includes(normalized);
}

function isPartnerPeer(peer: BackendPeer): boolean {
  if (isHubLikePeer(peer.node_type)) return true;
  const nodeType = String(peer.node_type || "").trim();
  const hasIdentity = Boolean(String(peer.node_id || "").trim() || String(peer.api_base_url || "").trim());
  return nodeType === "" && hasIdentity;
}

function connectionStateFromAck(raw: string): Connection["state"] {
  const state = String(raw || "").toUpperCase();
  if (state === "ACTIVE" || state === "ACCEPTED") return "active";
  if (state.includes("PENDING")) return "pending";
  return "disconnected";
}

/* ---------------- helpers ---------------- */

const pill = (state: Connection["state"]) => {
  if (state === "active")   return { label: "Connected",    cls: "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]" };
  if (state === "pending")  return { label: "Pending",      cls: "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]" };
  return { label: "Disconnected", cls: "border-destructive/30 bg-destructive/10 text-destructive" };
};

type TabId = "overview" | "partners" | "invites";

const TONE: Record<string, string> = {
  violet: "text-[oklch(0.70_0.18_270)]",
  mint:   "text-[oklch(0.80_0.18_145)]",
  cyan:   "text-[oklch(0.78_0.16_200)]",
  amber:  "text-[oklch(0.82_0.17_80)]",
};

function StatChip({
  label, value, hint, icon: Icon, tone,
}: { label: string; value: number; hint: string; icon: LucideIcon; tone: keyof typeof TONE }) {
  return (
    <div className="glass flex items-center gap-3 rounded-xl px-4 py-3">
      <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg border hairline bg-soft", TONE[tone])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tracking-tight text-foreground">{value}</span>
          <span className="truncate text-[11px] text-muted-foreground">{hint}</span>
        </div>
      </div>
    </div>
  );
}

function ConnectPage() {
  const { user } = useAuth();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [peerPool, setPeerPool] = useState<PeerCard[]>([]);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [wizard, setWizard] = useState(false);
  const [tab, setTab] = useState<TabId>("overview");
  const [identity, setIdentity] = useState({
    company: user?.daemon?.company || "",
    operator: user?.daemon?.userName || user?.name || "",
    email: user?.daemon?.email || user?.email || "",
    hubID: "",
    clientID: user?.id || "",
    nodeType: user?.daemon?.entityType || "hybrid",
  });

  async function refresh() {
    setBusy(true);
    try {
      const [peerRes, incomingRes, requestsRes, capacityRes, runtimeRes, profileRes] = await Promise.all([
        apiRequest<{ peers?: BackendPeer[] }>("/api/v1/network/peers/available", { method: "GET", timeoutMs: 12000 }),
        apiRequest<{ requests?: IncomingRequestRow[] }>("/api/v1/connections/requests/incoming", { method: "GET", timeoutMs: 12000 }),
        apiRequest<{ requests?: ConnectionRequestRow[] }>("/api/v1/connections/requests", { method: "GET", timeoutMs: 12000 }).catch(() => ({ requests: [] })),
        apiRequest<{ entries?: CapacityEntry[] }>("/api/v1/capacity", { method: "GET", timeoutMs: 12000 }).catch(() => ({ entries: [] })),
        apiRequest<{ hub_id?: string }>("/api/v1/runtime/health", { method: "GET", timeoutMs: 8000 }).catch(() => ({} as { hub_id?: string })),
        apiRequest<{ company_name?: string; user_name?: string; user_contact?: string; company_type?: string }>(
          "/admin/v1/spoke/profile/identity",
          { method: "GET", timeoutMs: 8000 },
        ).catch(() => ({} as { company_name?: string; user_name?: string; user_contact?: string; company_type?: string })),
      ]);

      const peers = (Array.isArray(peerRes.peers) ? peerRes.peers : []).filter((peer) => isPartnerPeer(peer));
      const byPeer = new Map<string, BackendPeer>();
      peers.forEach((p) => {
        const key = String(p.peer_id || "").trim();
        if (key) byPeer.set(key, p);
      });

      const peerLabelByPeerID = new Map<string, string>();
      await Promise.all(peers.map(async (peer) => {
        const peerID = String(peer.peer_id || "").trim();
        if (!peerID) return;
        const account = await fetchDaemonAccount(peer.api_base_url);
        const label = daemonAccountDisplayName(account);
        if (label) peerLabelByPeerID.set(peerID, label);
      }));

      const liveSpokes = new Set<string>();
      const entries = Array.isArray(capacityRes.entries) ? capacityRes.entries : [];
      entries.forEach((entry) => {
        const signal = entry?.signal;
        const spokeID = String(signal?.spoke_id || "").trim();
        const availCount = Number(signal?.avail_count || 0);
        if (spokeID && availCount > 0) liveSpokes.add(spokeID);
      });

      const requests = Array.isArray(requestsRes.requests) ? requestsRes.requests : [];
      const nextConnections: Connection[] = requests.map((request) => {
        const rawState = String(request.state || "").toUpperCase();
        const direction = String(request.direction || "outgoing").toLowerCase();
        const partnerPeerID = String(request.partner_peer_id || request.target_peer_id || request.source_peer_id || "").trim();
        const peer = byPeer.get(partnerPeerID);
        const fallbackPartner =
          String(peer?.node_id || "").trim() ||
          String(request.source_hub_id || "").trim() ||
          partnerPeerID ||
          String(request.request_id || "").trim();
        const partner =
          (partnerPeerID ? peerLabelByPeerID.get(partnerPeerID) : "") ||
          fallbackPartner;
        const isDeclined = rawState === "DECLINED" || rawState === "REVOKED";
        return {
          id: String(request.ack_id || request.request_id || `${partner}-${rawState}`).trim(),
          spokeID: partnerPeerID,
          peerID: partnerPeerID,
          partner: partner || "—",
          company: direction === "incoming"
            ? "Incoming connection"
            : "Outgoing connection",
          region: peer ? regionFromPeer(peer) : "—",
          available: partnerPeerID ? liveSpokes.has(partnerPeerID) : false,
          state: connectionStateFromAck(rawState),
          revoked: isDeclined,
          lastChecked: fmtWhen(request.updated_at || request.created_at || peer?.last_seen),
        };
      });
      // Resilience: ensure live connected peers are always represented, even when
      // request-history rows are stale or missing for the active account scope.
      const livePeerConnections: Connection[] = peers
        .filter((peer) => {
          const peerID = String(peer.peer_id || "").trim();
          return Boolean(peerID) && !!peer.connected;
        })
        .map((peer) => {
          const peerID = String(peer.peer_id || "").trim();
          const partner = peerLabelByPeerID.get(peerID) || String(peer.node_id || "").trim() || peerID;
          return {
            id: `live:${peerID}`,
            spokeID: peerID,
            peerID,
            partner,
            company: "Live network peer",
            region: regionFromPeer(peer),
            available: true,
            state: "active",
            revoked: false,
            lastChecked: fmtWhen(peer.last_seen),
          } satisfies Connection;
        });

      const stateRank: Record<Connection["state"], number> = {
        disconnected: 0,
        pending: 1,
        active: 2,
      };
      const mergedByPartner = new Map<string, Connection>();
      for (const row of [...nextConnections, ...livePeerConnections]) {
        const key = String(row.peerID || row.spokeID || row.partner || row.id).trim().toLowerCase();
        if (!key) continue;
        const existing = mergedByPartner.get(key);
        if (!existing) {
          mergedByPartner.set(key, row);
          continue;
        }
        if (stateRank[row.state] > stateRank[existing.state]) {
          mergedByPartner.set(key, row);
          continue;
        }
        if (stateRank[row.state] < stateRank[existing.state]) continue;
        if (!existing.available && row.available) {
          mergedByPartner.set(key, row);
        }
      }
      setConnections(Array.from(mergedByPartner.values()));

      const nextPeerPool: PeerCard[] = peers
        .flatMap((p) => {
          const peerID = String(p.peer_id || "").trim();
          const nodeID = String(p.node_id || "").trim();
          const id = peerID || nodeID;
          if (!id) return [];
          const nodeType = String(p.node_type || "").trim();
          const fallbackType = nodeType || "hybrid";
          const connected = !!p.connected;
          const displayName = (peerID ? peerLabelByPeerID.get(peerID) : "") || nodeID || peerID;
          const companyBits = [`${fallbackType} node`];
          if (nodeID && (!displayName || displayName !== nodeID)) {
            companyBits.push(nodeID);
          }
          return [{
            id,
            name: displayName,
            company: companyBits.join(" · "),
            region: regionFromPeer(p),
            gpus: undefined,
            latencyMs: undefined,
            online: connected,
          } satisfies PeerCard];
        });
      setPeerPool(nextPeerPool);

      const incoming = Array.isArray(incomingRes.requests) ? incomingRes.requests : [];
      const nextInvites: Invite[] = incoming
        .filter((r) => String(r.state || "").toLowerCase() === "pending")
        .map((r) => {
          const fromPeerID = String(r.from_peer_id || "").trim();
          const fromHubID = String(r.from_hub_id || "").trim();
          const peer = fromPeerID ? byPeer.get(fromPeerID) : undefined;
          const from =
            (fromPeerID ? peerLabelByPeerID.get(fromPeerID) : "") ||
            String(peer?.node_id || "").trim() ||
            fromHubID ||
            fromPeerID ||
            "—";
          return {
            id: r.request_id,
            from,
            company: peer?.node_type ? `${peer.node_type} node` : "—",
            region: peer ? regionFromPeer(peer) : "—",
            received: humanSince(r.received_at),
          };
        });
      setInvites(nextInvites);

      setIdentity((prev) => ({
        company: String(profileRes.company_name || prev.company || "").trim() || prev.company,
        operator: String(profileRes.user_name || prev.operator || "").trim() || prev.operator,
        email: String(profileRes.user_contact || prev.email || "").trim() || prev.email,
        hubID: String(runtimeRes.hub_id || prev.hubID || "").trim(),
        clientID: prev.clientID || String(user?.id || "").trim(),
        nodeType: (["hub", "spoke", "hybrid"].includes(String(profileRes.company_type || "").toLowerCase())
          ? String(profileRes.company_type).toLowerCase()
          : prev.nodeType) as "hub" | "spoke" | "hybrid",
      }));
    } catch (err) {
      if (err instanceof APIError) {
        toast.error(err.detail || "Failed to refresh connections");
      } else {
        toast.error("Failed to refresh connections");
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const stats = useMemo(() => {
    const visible = connections.filter((c) => !c.revoked);
    const ready = visible.filter((c) => c.state === "active").length;
    const live = new Set(visible.filter((c) => c.available).map((c) => c.spokeID || c.partner)).size;
    const pendingOutgoing = visible.filter((c) => c.state === "pending").length;
    return {
      partners: visible.length,
      ready,
      live,
      invites: invites.length + pendingOutgoing,
    };
  }, [connections, invites]);

  const visibleConnections = useMemo(() => connections.filter((c) => !c.revoked), [connections]);

  const filtered = visibleConnections.filter((c) =>
    [c.partner, c.company, c.region].some((v) => v.toLowerCase().includes(query.toLowerCase())),
  );

  async function accept(id: string) {
    try {
      await apiRequest(`/api/v1/connections/requests/${encodeURIComponent(id)}/accept`, { method: "POST" });
      toast.success("Invite accepted");
      await refresh();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to accept invite");
      else toast.error("Failed to accept invite");
    }
  }

  async function decline(id: string) {
    try {
      await apiRequest(`/api/v1/connections/requests/${encodeURIComponent(id)}/decline`, { method: "POST", body: { reason: "Declined from UI" } });
      toast.success("Invite declined");
      await refresh();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to decline invite");
      else toast.error("Failed to decline invite");
    }
  }

  const mapPins: MapPin[] = useMemo(
    () =>
      visibleConnections.map((c) => {
        const base = REGION_COORDS[c.region] ?? REGION_COORDS.Global;
        return {
          id: c.id,
          label: c.partner,
          region: c.region,
          lng: base.lng,
          lat: base.lat,
          status: c.state === "active" && c.available ? "live" : c.state === "pending" ? "pending" : "down",
        };
      }),
    [visibleConnections],
  );

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: "overview", label: "Overview" },
    { id: "partners", label: "Partners", count: stats.partners },
    { id: "invites",  label: "Invites",  count: invites.length },
  ];

  return (
    <>
      <Topbar title="Connect" subtitle="Step 2 · Bring partners online so this hub can route work and accept capacity." />
      <main className="flex-1 space-y-6 p-6">
        {/* Hero card — Connect partner CTA only */}
        <section className="glass relative flex items-center justify-center overflow-hidden rounded-2xl px-6 py-10">
          <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full opacity-30 blur-3xl" style={{ background: "var(--gradient-primary)" }} />
          <div aria-hidden className="pointer-events-none absolute -right-24 -bottom-24 h-64 w-64 rounded-full opacity-20 blur-3xl" style={{ background: "var(--gradient-primary)" }} />
          <button
            disabled={busy}
            onClick={() => setWizard(true)}
            className="relative inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-50"
            style={{ background: "var(--gradient-primary)" }}
          >
            <Plus className="h-4 w-4" /> Connect partner
          </button>
        </section>

        {/* Stat cards (compact) */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatChip label="Partners"      value={stats.partners} icon={Plug}        hint="Excludes revoked"  tone="violet" />
          <StatChip label="Ready"         value={stats.ready}    icon={ShieldCheck} hint="ACK active"        tone="mint" />
          <StatChip label="Live capacity" value={stats.live}     icon={Activity}    hint="Spokes broadcasting" tone="cyan" />
          <StatChip label="Invites"       value={stats.invites}  icon={Inbox}       hint="Awaiting response" tone="amber" />
        </div>

        {/* Sub tabs */}
        <div className="flex items-center gap-1 rounded-full border hairline bg-soft p-1 w-fit">
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition",
                  active ? "text-white shadow-[var(--shadow-glow)]" : "text-muted-foreground hover:text-foreground",
                )}
                style={active ? { background: "var(--gradient-primary)" } : undefined}
              >
                {t.label}
                {t.count !== undefined && (
                  <span className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px]",
                    active ? "bg-white/20" : "border hairline bg-background",
                  )}>{t.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* TAB: Overview */}
        {tab === "overview" && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <GlassPanel
              title="Global footprint"
              subtitle={`${stats.partners} mapped connections across ${new Set(visibleConnections.map((c) => c.region)).size} regions`}
              className="lg:col-span-2"
            >
              <WorldMap pins={mapPins} height={340} />
              <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[var(--success)]" /> Live
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[var(--warning)]" /> Pending
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-destructive" /> Offline
                </span>
              </div>
            </GlassPanel>

            <GlassPanel title="Region breakdown" subtitle="Capacity by geography">
              <div className="space-y-3">
                {Array.from(new Set(visibleConnections.map((c) => c.region))).map((r) => {
                  const count = visibleConnections.filter((c) => c.region === r).length;
                  const live  = visibleConnections.filter((c) => c.region === r && c.available).length;
                  const pct = (live / Math.max(count, 1)) * 100;
                  return (
                    <div key={r}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="inline-flex items-center gap-1.5 font-medium">
                          <Globe2 className="h-3 w-3 text-primary" />{r}
                        </span>
                        <span className="text-muted-foreground">{live}/{count} live</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--muted)]">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "var(--gradient-primary)" }} />
                      </div>
                    </div>
                  );
                })}
                {visibleConnections.length === 0 && (
                  <p className="text-xs text-muted-foreground">No partner regions discovered yet.</p>
                )}
              </div>
            </GlassPanel>
          </div>
        )}

        {/* TAB: Partners */}
        {tab === "partners" && (
          <GlassPanel
            title="Connected partners"
            subtitle={`${stats.partners} total · ${stats.ready} active`}
            action={
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search partners…"
                  className="h-8 w-56 rounded-full border hairline bg-soft pl-8 pr-3 text-xs outline-none placeholder:text-muted-foreground focus:border-primary/40"
                />
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-2 py-2.5 text-left font-medium">Partner</th>
                    <th className="px-2 py-2.5 text-left font-medium">Region</th>
                    <th className="px-2 py-2.5 text-left font-medium">Availability</th>
                    <th className="px-2 py-2.5 text-left font-medium">Status</th>
                    <th className="px-2 py-2.5 text-left font-medium">Last checked</th>
                    <th className="px-2 py-2.5 text-right font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const p = pill(c.state);
                    return (
                      <tr key={c.id} className="border-b hairline transition hover:bg-soft">
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg border hairline bg-soft text-[11px] font-semibold uppercase">
                              {c.partner.slice(0, 2)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium leading-tight">{c.partner}</p>
                              <p className="text-[11px] text-muted-foreground">{c.company}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><MapPinIcon className="h-3 w-3" />{c.region}</span>
                        </td>
                        <td className="px-2 py-3">
                          {c.available ? (
                            <span className="inline-flex items-center gap-1.5 text-xs">
                              <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--success)] opacity-60" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--success)]" />
                              </span>
                              Live now
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" /> Checking availability
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium", p.cls)}>
                            {p.label}
                          </span>
                        </td>
                        <td className="px-2 py-3 text-xs text-muted-foreground">{c.lastChecked}</td>
                        <td className="px-2 py-3 text-right">
                          <button className="inline-flex h-7 w-7 items-center justify-center rounded-full border hairline bg-soft text-muted-foreground hover:text-foreground">
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="py-10 text-center text-xs text-muted-foreground">No partners match “{query}”.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassPanel>
        )}

        {/* TAB: Invites */}
        {tab === "invites" && (
          <GlassPanel
            title="Pending invites"
            subtitle="Accept to begin receiving capacity from these partners"
            action={<span className="rounded-full border hairline bg-soft px-2 py-0.5 text-[11px] text-muted-foreground">{invites.length} new</span>}
          >
            {invites.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border hairline bg-soft">
                  <Inbox className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="mt-3 text-sm font-medium">No invites waiting</p>
                <p className="mt-1 text-xs text-muted-foreground">Partners you invite will appear here once they respond.</p>
              </div>
            ) : (
              <ul className="divide-y hairline">
                {invites.map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl border hairline bg-soft">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{inv.from}</p>
                        <p className="text-xs text-muted-foreground">{inv.company} · {inv.region} · {inv.received}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => decline(inv.id)}
                        className="rounded-full border hairline bg-soft px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => accept(inv.id)}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)]"
                        style={{ background: "var(--gradient-primary)" }}
                      >
                        <CheckCircle2 className="h-3 w-3" /> Accept
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </GlassPanel>
        )}
      </main>

      {wizard && (
        <ConnectWizard
          peers={peerPool}
          identity={identity}
          onClose={() => setWizard(false)}
          onSent={async () => {
            setWizard(false);
            await refresh();
          }}
        />
      )}
    </>
  );
}

/* ---------------- Wizard ---------------- */

function ConnectWizard({
  onClose,
  peers,
  identity,
  onSent,
}: {
  onClose: () => void;
  peers: PeerCard[];
  identity: {
    company: string;
    operator: string;
    email: string;
    hubID: string;
    clientID: string;
    nodeType: "hub" | "spoke" | "hybrid";
  };
  onSent: () => Promise<void>;
}) {
  const [step, setStep] = useState(1);
  const [peer, setPeer] = useState<PeerCard | null>(null);
  const [rules, setRules] = useState<"none" | "custom">("none");
  const [ruleRegions, setRuleRegions] = useState("");
  const [ruleTiers, setRuleTiers] = useState("");
  const [ruleCerts, setRuleCerts] = useState("");
  const [ruleCCRequired, setRuleCCRequired] = useState(false);
  const [ruleMinCertDays, setRuleMinCertDays] = useState("0");
  const [sending, setSending] = useState(false);
  const [identityDraft, setIdentityDraft] = useState(() => ({ ...identity }));

  const steps = ["Choose partner", "Confirm identity", "Access rules"] as const;
  const identityInputClass =
    "mt-1 h-8 w-full rounded-md border hairline bg-background px-2 text-xs outline-none focus:border-primary/40";

  useEffect(() => {
    setIdentityDraft({ ...identity });
  }, [identity.company, identity.operator, identity.email, identity.hubID, identity.clientID, identity.nodeType]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-md" onClick={onClose} />
      <div className="glass-strong relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl">
        <header className="flex items-start justify-between border-b hairline p-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Connect partner</p>
            <h2 className="mt-1 font-display text-lg font-semibold tracking-tight">{steps[step - 1]}</h2>
          </div>
          <button onClick={onClose} className="rounded-full border hairline bg-soft p-1.5 text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="flex items-center gap-2 border-b hairline px-5 py-3">
          {steps.map((s, i) => {
            const n = i + 1;
            const active = n === step;
            const done = n < step;
            return (
              <div key={s} className="flex flex-1 items-center gap-2">
                <span className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold transition",
                  done ? "border-transparent text-white" : active ? "border-primary text-primary" : "hairline text-muted-foreground",
                )} style={done ? { background: "var(--gradient-primary)" } : undefined}>
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
                </span>
                <span className={cn("text-xs font-medium", active ? "text-foreground" : "text-muted-foreground")}>{s}</span>
                {i < steps.length - 1 && <div className="ml-1 h-px flex-1 bg-[var(--border)]" />}
              </div>
            );
          })}
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          {step === 1 && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {peers.length > 0 ? peers.map((p) => {
                const selected = peer?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPeer(p)}
                    className={cn(
                      "group relative overflow-hidden rounded-xl border bg-soft p-4 text-left transition",
                      selected ? "border-primary shadow-[var(--shadow-glow)]" : "hairline hover:border-primary/40",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg border hairline bg-background text-xs font-semibold uppercase">
                          {p.name.slice(0,2)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold leading-tight">{p.name}</p>
                          <p className="text-[11px] text-muted-foreground">{p.company}</p>
                        </div>
                      </div>
                      {p.online ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[var(--success)]/30 bg-[var(--success)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--success)]">
                          <ShieldCheck className="h-2.5 w-2.5" /> Online
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[var(--warning)]/30 bg-[var(--warning)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--warning)]">
                          <Clock className="h-2.5 w-2.5" /> Offline
                        </span>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                      <div><p className="text-muted-foreground">Region</p><p className="font-medium">{p.region}</p></div>
                      <div><p className="text-muted-foreground">GPUs</p><p className="font-medium">{typeof p.gpus === "number" ? p.gpus : "—"}</p></div>
                      <div><p className="text-muted-foreground">Latency</p><p className="font-medium">{typeof p.latencyMs === "number" ? `${p.latencyMs} ms` : "—"}</p></div>
                    </div>
                  </button>
                );
              }) : (
                <div className="col-span-full rounded-xl border hairline bg-soft p-4 text-sm text-muted-foreground">
                  No discoverable peers yet. Ensure gossip/bootstrap is running, then refresh this page.
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Editable identity details. The partner sees this when reviewing the invite.</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Building2 className="h-3 w-3" />Company</p>
                  <input
                    value={identityDraft.company}
                    onChange={(e) => setIdentityDraft((prev) => ({ ...prev, company: e.target.value }))}
                    className={identityInputClass}
                    placeholder="Company name"
                  />
                </div>
                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><User className="h-3 w-3" />Operator</p>
                  <input
                    value={identityDraft.operator}
                    onChange={(e) => setIdentityDraft((prev) => ({ ...prev, operator: e.target.value }))}
                    className={identityInputClass}
                    placeholder="Operator name"
                  />
                </div>
                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Hash className="h-3 w-3" />Hub ID</p>
                  <input
                    value={identityDraft.hubID}
                    onChange={(e) => setIdentityDraft((prev) => ({ ...prev, hubID: e.target.value }))}
                    className={cn(identityInputClass, "font-mono")}
                    placeholder="Hub ID"
                  />
                </div>
                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Hash className="h-3 w-3" />Client ID</p>
                  <textarea
                    value={identityDraft.clientID}
                    onChange={(e) => setIdentityDraft((prev) => ({ ...prev, clientID: e.target.value }))}
                    className="mt-1 min-h-[3.25rem] w-full resize-none rounded-md border hairline bg-background px-2 py-1.5 font-mono text-xs break-all outline-none focus:border-primary/40"
                    placeholder="Client ID"
                    rows={2}
                  />
                </div>
                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Mail className="h-3 w-3" />Email</p>
                  <input
                    value={identityDraft.email}
                    onChange={(e) => setIdentityDraft((prev) => ({ ...prev, email: e.target.value }))}
                    className={identityInputClass}
                    placeholder="Email address"
                  />
                </div>
                <div className="rounded-xl border hairline bg-soft p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Sparkles className="h-3 w-3" />Type</p>
                  <select
                    value={identityDraft.nodeType}
                    onChange={(e) => setIdentityDraft((prev) => ({ ...prev, nodeType: e.target.value as "hub" | "spoke" | "hybrid" }))}
                    className={identityInputClass}
                  >
                    <option value="hub">HUB · Live</option>
                    <option value="spoke">SPOKE · Live</option>
                    <option value="hybrid">HYBRID · Live</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Decide how strict this connection should be. You can change rules anytime.</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {([
                  { id: "none",   title: "No specific requirements", desc: "Accept any region, tier, or compliance posture this partner offers." },
                  { id: "custom", title: "Custom access rules",      desc: "Pin regions, data tiers, certifications, CC mode and minimum cert age." },
                ] as const).map((opt) => {
                  const sel = rules === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setRules(opt.id)}
                      className={cn(
                        "rounded-xl border p-4 text-left transition",
                        sel ? "border-primary bg-soft shadow-[var(--shadow-glow)]" : "hairline bg-soft hover:border-primary/40",
                      )}
                    >
                      <p className="text-sm font-semibold">{opt.title}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>

              {rules === "custom" && (
                <div className="grid grid-cols-2 gap-3 rounded-xl border hairline bg-soft p-4 md:grid-cols-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Regions</p>
                    <input
                      value={ruleRegions}
                      onChange={(e) => setRuleRegions(e.target.value)}
                      className="mt-1 h-8 w-full rounded-md border hairline bg-background px-2 text-xs outline-none focus:border-primary/40"
                      placeholder="GB,DE,FR"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Data tiers</p>
                    <input
                      value={ruleTiers}
                      onChange={(e) => setRuleTiers(e.target.value)}
                      className="mt-1 h-8 w-full rounded-md border hairline bg-background px-2 text-xs outline-none focus:border-primary/40"
                      placeholder="OPEN,INTERNAL"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Certifications</p>
                    <input
                      value={ruleCerts}
                      onChange={(e) => setRuleCerts(e.target.value)}
                      className="mt-1 h-8 w-full rounded-md border hairline bg-background px-2 text-xs outline-none focus:border-primary/40"
                      placeholder="ISO_27001,SOC2"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">CC mode</p>
                    <select
                      value={ruleCCRequired ? "required" : "optional"}
                      onChange={(e) => setRuleCCRequired(e.target.value === "required")}
                      className="mt-1 h-8 w-full rounded-md border hairline bg-background px-2 text-xs outline-none focus:border-primary/40"
                    >
                      <option value="optional">Optional</option>
                      <option value="required">Required</option>
                    </select>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Min cert days</p>
                    <input
                      type="number"
                      min={0}
                      value={ruleMinCertDays}
                      onChange={(e) => setRuleMinCertDays(e.target.value)}
                      className="mt-1 h-8 w-full rounded-md border hairline bg-background px-2 text-xs outline-none focus:border-primary/40"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between border-t hairline p-4">
          <div className="text-[11px] text-muted-foreground">
            {step === 1 && (peer ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-[var(--success)]" /> {peer.name} selected</span> : "Pick a partner to continue")}
            {step === 2 && <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-primary" /> Identity confirmed</span>}
            {step === 3 && <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-[var(--warning)]" /> You can revise rules later</span>}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep((s) => s - 1)} className="rounded-full border hairline bg-soft px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">Back</button>
            )}
            {step < 3 ? (
              <button
                disabled={step === 1 && !peer}
                onClick={() => setStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-40"
                style={{ background: "var(--gradient-primary)" }}
              >
                Continue <ArrowRight className="h-3 w-3" />
              </button>
            ) : (
              <button
                disabled={!peer || sending}
                onClick={async () => {
                  if (!peer || sending) return;
                  setSending(true);
                  try {
                    const body: Record<string, unknown> = {
                      target_peer_id: peer.id,
                      hub_identity: {
                        company_name: String(identityDraft.company || "").trim(),
                        user_name: String(identityDraft.operator || "").trim(),
                        user_contact: String(identityDraft.email || "").trim(),
                        company_type: String(identityDraft.nodeType || "hybrid").trim().toLowerCase(),
                      },
                    };
                    if (rules === "custom") {
                      const parseCSV = (raw: string) =>
                        raw.split(",").map((v) => v.trim()).filter((v) => v.length > 0);
                      const minDays = Math.max(0, Number(ruleMinCertDays || 0));
                      body.access_rules = {
                        required_certifications: parseCSV(ruleCerts),
                        min_cert_remaining_days: Number.isFinite(minDays) ? minDays : 0,
                        accepted_regions: parseCSV(ruleRegions),
                        min_data_tiers_accepted: parseCSV(ruleTiers),
                        cc_mode_required: !!ruleCCRequired,
                      };
                    }
                    await apiRequest("/api/v1/connections/requests", {
                      method: "POST",
                      body,
                      timeoutMs: 15000,
                    });
                    toast.success("Connection invite sent");
                    await onSent();
                  } catch (err) {
                    if (err instanceof APIError) toast.error(err.detail || "Failed to send invite");
                    else toast.error("Failed to send invite");
                  } finally {
                    setSending(false);
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-50"
                style={{ background: "var(--gradient-primary)" }}
              >
                {sending ? "Sending…" : "Send invite"} <ArrowRight className="h-3 w-3" />
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
